import React from 'react'
import { useComposedRefs } from '../utils/compose-refs'

export type FileProcessingProgress = { completed: number; total: number }

/**
 * Takes over a whole multi-file selection. Without one, `processFile` runs one file at a time —
 * all at once could pin enough memory to get a webview killed mid-form (#25423).
 */
export type ProcessFilesFunction = (
  files: File[],
  onProgress?: (completed: number, total: number) => unknown
) => Promise<File[]>

export type HeadlessFileInputProps = {
  children?: React.ReactNode
  /**
   * Multi-select only. Trims to remaining capacity *before* `processFile` runs. Only Files count: string
   * entries are already-uploaded storage URLs, not files being attached. Undefined means no cap.
   */
  maxFiles?: number
} & (
  | {
      multiple: true
      value?: (string | File)[]
      defaultValue?: (string | File)[]
      onChange?(value: (string | File)[]): unknown
    }
  | {
      multiple?: false
      value?: string | File | null
      defaultValue?: string | File | null
      onChange?(value: string | File | null): unknown
    }
)

type FileInputContext = { maxFiles?: number } & (
  | { multiple?: false; value: File | string | null; onChange(value: File | string | null): unknown }
  | { multiple: true; value: (File | string)[]; onChange(value: (File | string)[]): unknown }
)

const fileInputContext = React.createContext<FileInputContext>({
  multiple: false,
  value: null,
  onChange: () => null
})

function FileInputContainer({ multiple, value, defaultValue, onChange, maxFiles, children }: HeadlessFileInputProps) {
  const controlled = useControlled({ multiple, value, defaultValue, onChange }) as FileInputContext
  return (
    <fileInputContext.Provider value={{ ...controlled, maxFiles } as FileInputContext}>{children}</fileInputContext.Provider>
  )
}

const FileInputDropZone = React.forwardRef<
  HTMLLabelElement,
  {
    name?: string
    children: ((props: { dragOver: boolean }) => React.ReactElement<any>) | React.ReactElement<any>
    inputId?: string
    accept?: string | undefined
    onDragChange?(value: boolean): unknown
    capture?: React.InputHTMLAttributes<'file'>['capture']
    disabled?: React.InputHTMLAttributes<'file'>['disabled']
    processFile?: (file: File) => Promise<File>
    processFiles?: ProcessFilesFunction
    /** Fires when `maxFiles` discarded part of a selection. */
    onFilesTrimmed?(discardedCount: number): unknown
    /** Fires as a selection is processed, then once with `null` when it settles. */
    onProcessingProgress?(progress: FileProcessingProgress | null): unknown
    inputRef?: React.Ref<HTMLInputElement>
    /**
     * Wiring for the real `<input type="file">` this drop zone hides inside itself. It has to land on
     * the input rather than on the wrapping label, which only supplies the accessible name — without
     * it the help and validation text rendered under the field is never announced.
     */
    inputDescribedBy?: string
    inputIsInvalid?: boolean
    /** Accessible name for that input, for a drop zone with no visible label to borrow one from. */
    inputAriaLabel?: string
  } & Omit<React.ComponentProps<'label'>, 'children'>
>(
  (
    {
      children,
      inputId,
      inputRef,
      onDragChange,
      name,
      accept,
      capture,
      disabled,
      processFile,
      processFiles,
      onFilesTrimmed,
      onProcessingProgress,
      inputDescribedBy,
      inputIsInvalid,
      inputAriaLabel,
      ...props
    },
    ref
  ) => {
    const { onChange, value, multiple, maxFiles } = React.useContext(fileInputContext)
    const [dragOver, setDragOver] = React.useState(false)
    const startDrag = React.useCallback(
      (e: React.DragEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setDragOver(true)
        onDragChange && onDragChange(true)
      },
      [setDragOver, onDragChange]
    )
    const stopDrag = React.useCallback(
      (e: React.DragEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setDragOver(false)
        onDragChange && onDragChange(false)
      },
      [setDragOver, onDragChange]
    )

    const childrenResult = typeof children === 'function' ? children({ dragOver }) : children
    const cloned = React.cloneElement(childrenResult, {
      ...childrenResult.props,
      children: [
        ...React.Children.toArray(childrenResult.props.children),
        <FileInputInput
          key="file-input-hidden"
          id={inputId}
          name={name}
          accept={accept}
          aria-describedby={inputDescribedBy}
          aria-label={inputAriaLabel}
          aria-invalid={inputIsInvalid || undefined}
          ref={inputRef}
          style={{ width: '0.1px', height: '0.1px', opacity: 0, overflow: 'hidden', position: 'absolute', zIndex: -10 }}
          capture={capture}
          disabled={disabled}
          processFile={processFile}
          processFiles={processFiles}
          onFilesTrimmed={onFilesTrimmed}
          onProcessingProgress={onProcessingProgress}
        />
      ]
    })

    return (
      // @ts-ignore
      <label {...props} type="" ref={ref}>
        <div
          onDrag={cancel}
          onDragStart={cancel}
          onDragOver={cancel}
          onDragEnter={startDrag}
          onDragLeave={stopDrag}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragOver(false)
            onDragChange && onDragChange(false)

            if (e.dataTransfer.files.length) {
              if (multiple) {
                const dropped = Array.from(e.dataTransfer.files)
                const accepted = trimToRemainingCapacity(dropped, value, maxFiles)
                onFilesTrimmed?.(dropped.length - accepted.length)
                onChange([...value, ...accepted])
              } else {
                onChange(e.dataTransfer.files.item(0) || null)
              }
            } else {
              const uri = e.dataTransfer.getData('text/uri-list')
              if (uri) {
                fetchFileFromUri(uri).then((file) => {
                  if (multiple) {
                    onChange([...value, file])
                  } else {
                    onChange(file)
                  }
                })
              }
            }
          }}
        >
          {cloned}
        </div>
      </label>
    )
  }
)
FileInputDropZone.displayName = 'FileInputDropZone'

async function fetchFileFromUri(uri: string): Promise<File> {
  const res = await fetch(uri)
  const blob = await res.blob()
  const contentType = res.headers.get('content-type') || blob.type || ''
  const contentDisposition = res.headers.get('content-disposition')
  let filename = new URL(uri).pathname.split('/').pop() || 'file'
  const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/)
  if (match) filename = decodeURIComponent(match[1])
  return new File([blob], filename, { type: contentType })
}

function cancel(e: React.SyntheticEvent) {
  e.stopPropagation()
  e.preventDefault()
}

const FileInputInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<'input'> & {
    processFile?: (file: File) => Promise<File>
    processFiles?: ProcessFilesFunction
    onFilesTrimmed?(discardedCount: number): unknown
    onProcessingProgress?(progress: FileProcessingProgress | null): unknown
  }
>(({ name, processFile, processFiles, onFilesTrimmed, onProcessingProgress, form, ...props }, refProp) => {
  const { multiple, value, onChange, maxFiles } = React.useContext(fileInputContext)
  const localRef = React.useRef<HTMLInputElement>(null)
  const ref = useComposedRefs(localRef, refProp)

  React.useEffect(() => {
    if (localRef.current) {
      const dt = new DataTransfer()
      if (Array.isArray(value)) {
        value.forEach(async (v) => v instanceof File && dt.items.add(v))
      } else if (value && value instanceof File) {
        dt.items.add(value)
      }
      localRef.current.files = dt.files
    }
  }, [value, processFile])

  const urls = React.useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((v, i) => (typeof v === 'string' ? { name: `${name}.${i}`, value: v } : null)).filter(Boolean) as {
        name: string
        value: string
      }[]
    } else if (value && typeof value === 'string') {
      return [{ name: name, value: value }]
    } else {
      return []
    }
  }, [value, name])

  const hasFiles =
    typeof value !== 'string' &&
    !!value &&
    !(Array.isArray(value) && !value.filter((v) => v && typeof v !== 'string').length)
  const hasUrls = !!urls.length

  return (
    <>
      <input
        {...props}
        type="file"
        form={form}
        ref={hasFiles ? ref : undefined}
        name={hasFiles ? name : undefined}
        onChange={async (e) => {
          // Snapshot before the first await: React nulls `currentTarget` after dispatch.
          const selectedFiles = e.currentTarget.files
          const acceptedFrom = (files: FileList) => {
            const selected = Array.from(files)
            const accepted = trimToRemainingCapacity(selected, value as (string | File)[], maxFiles)
            onFilesTrimmed?.(selected.length - accepted.length)
            return accepted
          }
          try {
            const newValue = (
              multiple
                ? selectedFiles
                  ? [
                      ...value,
                      ...(await runFileProcessing(acceptedFrom(selectedFiles), {
                        processFile,
                        processFiles,
                        onProgress: onProcessingProgress
                      }))
                    ]
                  : []
                : processFile && selectedFiles?.item(0)
                  ? await processFile(selectedFiles.item(0) as File)
                  : selectedFiles?.item(0)
            ) as any

            onChange(newValue)
          } finally {
            onProcessingProgress?.(null)
          }
        }}
        multiple={multiple}
      />
      {urls.map((url, i) => (
        // Keyed by position, not by `url.name`: `name` is optional, and an unnamed input made every
        // key `undefined`, which React reads as no key at all.
        <input
          type="hidden"
          ref={i === 0 && !hasFiles ? ref : undefined}
          form={form}
          value={url.value}
          name={url.name}
          key={`file-input-url-${i}`}
        />
      ))}
      {!hasUrls && !hasFiles ? <input type="hidden" ref={ref} form={form} name={name} value="" /> : null}
    </>
  )
})
FileInputInput.displayName = 'FileInputInput'

function FilePreview({
  children
}: {
  children(
    props: {
      file: File | null
      fileUrl: string
      remove(): unknown
      replace(newFile: File | string): unknown
    }[]
  ): React.ReactNode
}) {
  const { value, onChange } = React.useContext(fileInputContext)
  const fileUrlFor = useFileObjectUrls()
  const removeSingle = React.useCallback(() => onChange(null as any), [onChange])
  const replaceSingle = React.useCallback((file: File | string) => onChange(file as any), [onChange])

  const removeMultiple = React.useCallback(
    (index: number) => onChange((value as File[]).filter((f, i) => i !== index) as any),
    [value, onChange]
  )
  const replaceMultiple = React.useCallback(
    (index: number, file: File | string) =>
      onChange((value as (File | string)[]).map((f, i) => (i === index ? file : f)) as any),
    [value, onChange]
  )

  if (!value) {
    return <>{children([])}</>
  } else if (Array.isArray(value)) {
    return (
      <>
        {children(
          value.map((v, i) => ({
            file: typeof v === 'string' ? null : v,
            fileUrl: fileUrlFor(v),
            remove: () => removeMultiple(i),
            replace: (f) => replaceMultiple(i, f)
          }))
        )}
      </>
    )
  } else {
    return (
      <>
        {children([
          {
            file: typeof value === 'string' ? null : value,
            fileUrl: fileUrlFor(value),
            remove: removeSingle,
            replace: replaceSingle
          }
        ])}
      </>
    )
  }
}

function useControlled<T extends string | File | null | (string | File)[]>({
  defaultValue,
  value,
  onChange: onChangeRaw,
  multiple
}: {
  value?: T
  defaultValue?: T
  onChange?(value: null | string | File | (string | File)[]): unknown
  multiple?: boolean
}) {
  const [state, setState] = React.useState(defaultValue)

  const onChange = React.useCallback(
    (v: T) => {
      setState(v)
      onChangeRaw && onChangeRaw(v)
    },
    [onChangeRaw, setState]
  )

  const proposed = value === undefined ? state : value

  return { value: !proposed && multiple ? [] : proposed || null, onChange, multiple: !!multiple }
}

/**
 * Resolves a value to a displayable URL, minting at most one object URL per File instance.
 *
 * This used to call `URL.createObjectURL` inline on every render, which leaked a URL per render and
 * handed back a different string each time — and consumers use that string as their React `key` and
 * `<img src>`, so every preview was also torn down and refetched on each render. URLs are released
 * once their File leaves the value; the last few are left for document unload rather than revoked on
 * unmount, because a remount renders before effects run and would reference the revoked URLs.
 */
export function useFileObjectUrls() {
  const cache = React.useRef(new Map<File, string>())
  const stillPresent = new Set<File>()

  React.useEffect(() => {
    cache.current.forEach((url, file) => {
      if (!stillPresent.has(file)) {
        URL.revokeObjectURL(url)
        cache.current.delete(file)
      }
    })
  })

  return function fileUrlFor<T extends File | string | null>(f: T): Exclude<T, File> {
    if (!f) {
      return null as any
    } else if (typeof f === 'string') {
      return f as Exclude<T, File>
    }

    stillPresent.add(f)
    const cached = cache.current.get(f)
    if (cached) {
      return cached as Exclude<T, File>
    }

    const created = URL.createObjectURL(f)
    cache.current.set(f, created)
    return created as Exclude<T, File>
  }
}

// Avoids compressing files an over-cap selection will discard; consumers still enforce the cap on the result.
function trimToRemainingCapacity(files: File[], held: (string | File)[], maxFiles?: number) {
  if (maxFiles === undefined) return files
  const heldFileCount = held.filter((entry) => entry instanceof File).length
  return files.slice(0, Math.max(0, maxFiles - heldFileCount))
}

async function runFileProcessing(
  files: File[],
  {
    processFile,
    processFiles,
    onProgress
  }: {
    processFile?: (file: File) => Promise<File>
    processFiles?: ProcessFilesFunction
    onProgress?: (progress: FileProcessingProgress | null) => unknown
  }
) {
  if (!processFile && !processFiles) return files

  // Before the first file, not after: processing can freeze the main thread on iOS WebView (#25554).
  if (files.length) onProgress?.({ completed: 0, total: files.length })
  const report = (completed: number, total: number) => onProgress?.({ completed, total })

  if (processFiles) return processFiles(files, report)

  const processed: File[] = []
  for (const file of files) {
    processed.push(await processFile!(file))
    report(processed.length, files.length)
  }
  return processed
}

export const HeadlessFileInput = Object.assign(FileInputContainer, {
  Input: FileInputInput,
  Preview: FilePreview,
  DropZone: FileInputDropZone
})
